import { Button, ButtonRole, ButtonTargetKind } from "@components/Button";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { LoadingIndicator } from "@components/LoadingIndicator";
import { TextInputProps, TextInputType, Combobox } from "@components/TextInput";
import { css } from "@emotion/react";
import React, { useEffect } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

import { Location } from "@every.org/common/src/codecs/location";
import { assertEnvPresent } from "@every.org/common/src/helpers/getEnv";

import { DEFAULT_SEARCH_DISTANCE } from "src/context/SearchContext/helpers";
import { useScript, ScriptLoadStatus } from "src/hooks/useScript";
import { colorCssVars } from "src/theme/color";
import { INPUT_BORDER_RADIUS } from "src/theme/common";
import { spacing } from "src/theme/spacing";
import { logger } from "src/utility/logger";
import { getWindow } from "src/utility/window";

const suggestionItemCss = css`
  cursor: pointer;
  padding: ${spacing.xs} ${spacing.m};
  background-color: var(${colorCssVars.input.background.default});

  :first-of-type {
    margin-top: 1px;
  }
  :last-child {
    border-radius: 0 0 ${INPUT_BORDER_RADIUS} ${INPUT_BORDER_RADIUS};
  }
`;
const activeSuggestionItemCss = css`
  background-color: var(${colorCssVars.input.background.focus});
`;

const suggestionsContainerCss = css`
  position: absolute;
  width: 100%;
  z-index: 1;
`;

interface LocationSearchInputProps extends TextInputProps {
  value?: string;

  /**
   * Handles when the input text changes.
   */
  handleTextChange: (value: string) => void;

  /**
   * Called when the selected location is changed. When user types in
   * the field, it will call this with undefined.
   */
  onLocationChanged: (location?: Location) => void;
  showClearButton?: boolean;

  /**
   * When true, only allows street address searches. When false, allows broader
   * location searches including cities, states, and countries.
   */
  addressOnly?: boolean;
}

export default function LocationSearchInput({
  value,
  handleTextChange,
  onLocationChanged,
  showClearButton,
  addressOnly = false,
  placeholder = "Search for city or country",
  ...inputProps
}: LocationSearchInputProps) {
  const googleMapsSdkStatus = useScript(
    `https://maps.googleapis.com/maps/api/js?key=${assertEnvPresent(
      process.env.REACT_APP_PLACES_API_KEY ||
        process.env.NEXT_PUBLIC_PLACES_API_KEY,
      "PLACES_API_KEY"
    )}&libraries=places,geometry`
  );

  const handleChange = (text: string) => {
    handleTextChange(text);
    onLocationChanged(undefined);
  };

  // Cache the dummy map instance to avoid recreating it on every selection
  const cachedDummyMap = React.useRef<google.maps.Map | null>(null);
  const cachedPlacesService =
    React.useRef<google.maps.places.PlacesService | null>(null);

  const getDummyMapAndService = () => {
    const document = getWindow()?.document;
    if (!document) {
      return { map: null, service: null };
    }
    if (!cachedDummyMap.current) {
      cachedDummyMap.current = new google.maps.Map(
        document.createElement("div")
      );
    }
    if (!cachedPlacesService.current && cachedDummyMap.current) {
      cachedPlacesService.current = new google.maps.places.PlacesService(
        cachedDummyMap.current
      );
    }
    return {
      map: cachedDummyMap.current,
      service: cachedPlacesService.current,
    };
  };

  const handleSelect = async (address: string, placeId: string) => {
    handleTextChange(address);
    try {
      const geocodeResults = await geocodeByAddress(address);

      const result = geocodeResults[0];
      const addressComponents = result.address_components;
      const geolocation: {
        streetAddress?: string;
        city?: string;
        state?: string;
        country?: string;
        postalCode?: string;
      } = {};

      let streetNumber = "";
      let route = "";

      addressComponents.forEach((component) => {
        if (component.types.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (component.types.includes("route")) {
          route = component.long_name;
        } else if (component.types.includes("locality")) {
          geolocation.city = component.long_name;
        } else if (component.types.includes("administrative_area_level_1")) {
          geolocation.state = component.long_name;
        } else if (component.types.includes("country")) {
          geolocation.country = component.short_name;
        } else if (component.types.includes("postal_code")) {
          geolocation.postalCode = component.long_name;
        }
      });

      // Construct street address from components
      if (streetNumber || route) {
        geolocation.streetAddress = `${streetNumber} ${route}`.trim();
      }

      const latLng = await getLatLng(result);
      const { service } = getDummyMapAndService();

      const location: Location = {
        address,
        lat: latLng.lat,
        lng: latLng.lng,
        geolocation:
          geolocation.streetAddress ||
          geolocation.city ||
          geolocation.state ||
          geolocation.country ||
          geolocation.postalCode
            ? geolocation
            : undefined,
      };
      if (service) {
        service.getDetails({ placeId }, (place, status) => {
          if (
            place &&
            status === google.maps.places.PlacesServiceStatus.OK &&
            place.geometry?.viewport
          ) {
            const ne = place.geometry.viewport.getNorthEast();
            const sw = place.geometry.viewport.getSouthWest();
            location.bbNeLat = ne.lat();
            location.bbNeLng = ne.lng();
            location.bbSwLat = sw.lat();
            location.bbSwLng = sw.lng();
            onLocationChanged(location);
          } else {
            location.distance = DEFAULT_SEARCH_DISTANCE;
            onLocationChanged(location);
          }
        });
      } else {
        location.distance = DEFAULT_SEARCH_DISTANCE;
        onLocationChanged(location);
      }
    } catch (e) {
      logger.error({
        error: e,
        message:
          "Could not geocode address: " + address + " placeID: " + placeId,
      });
    }
  };

  const searchOptions = {
    types: addressOnly
      ? ["address"]
      : [
          "locality",
          "administrative_area_level_3",
          "country",
          "administrative_area_level_1",
        ],
  };

  useEffect(() => {
    if (googleMapsSdkStatus === ScriptLoadStatus.ERROR) {
      logger.error({
        message:
          "Could not load Google Maps SDK, cannot display places autocomplete",
      });
    }
  }, [googleMapsSdkStatus]);
  if (googleMapsSdkStatus === ScriptLoadStatus.LOADING) {
    return <LoadingIndicator />;
  }
  if (googleMapsSdkStatus === ScriptLoadStatus.ERROR) {
    return <React.Fragment />;
  }
  return (
    <PlacesAutocomplete
      value={value || ""}
      onChange={handleChange}
      onSelect={handleSelect}
      searchOptions={searchOptions}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps }) => {
        const { type, ...placesProps } = getInputProps();
        return (
          <Combobox
            {...inputProps}
            id={inputProps.name}
            type={TextInputType.TEXT}
            {...placesProps}
            className={[addressOnly && "rr-block", inputProps.className]
              .filter(Boolean)
              .join(" ")}
            placeholder={placeholder}
            inputPrefix={
              <Icon
                iconImport={() => import("@components/Icon/icons/SearchIcon")}
                css={{ marginRight: spacing.xs }}
                size={IconSize.MEDIUM}
                display={IconDisplay.SECONDARY}
              />
            }
            setInputPrefixColorAndHeight={false}
            inputSuffix={
              showClearButton &&
              !!value && (
                <Button
                  data-tname="LocationSearchInput--clear"
                  role={ButtonRole.UNSTYLED}
                  onClick={{
                    kind: ButtonTargetKind.FUNCTION,
                    action: (e) => {
                      e.stopPropagation();
                      onLocationChanged(undefined);
                      handleTextChange("");
                    },
                  }}
                >
                  <Icon
                    iconImport={() => import("@components/Icon/icons/XIcon")}
                    size={IconSize.MEDIUM}
                    display={IconDisplay.ACCENT}
                  />
                </Button>
              )
            }
            dropdown={
              <ul
                css={suggestionsContainerCss}
                className="autocomplete-dropdown-container"
              >
                {suggestions.length > 0 &&
                  suggestions.map((suggestion, i) => {
                    return (
                      <li
                        css={[
                          suggestionItemCss,
                          suggestion.active && activeSuggestionItemCss,
                        ]}
                        {...getSuggestionItemProps(suggestion)}
                        key={suggestion.placeId}
                      >
                        {suggestion.description}
                      </li>
                    );
                  })}
              </ul>
            }
            isDropdownActive={suggestions.length > 0}
            autoComplete="none"
          />
        );
      }}
    </PlacesAutocomplete>
  );
}
